import { env } from "../../lib/env.js";

// Single source of truth for anchoring image/asset URLs to the public host.
//
// Why this exists: crm-api returns RELATIVE URLs for web-portal use — tenant
// logos as `/api/v1/image/<key>.<ext>` (Tenant#logo_url), uploaded newsletter
// images the same, and the bundled stock photos as `/stock-images/*.jpg`. The
// dashboard SPA is served from a *different* origin than crm-api/the public
// site (`crm.xopnetworks.com` / `localhost`), so a raw relative `<img src>`
// resolves against the SPA origin — which has no such file — and renders a
// broken-image placeholder. Email HTML hits the same wall: clients dereference
// relative paths against their own origin.
//
// Every place that renders one of these URLs into a preview or an email must
// anchor it to the public asset host first. This mirrors the crm-api
// `Tenant#logo_url_absolute` fallback (xop-brain apps/crm-api/app/models/
// tenant.rb) so the dashboard and the backend agree on the same host.

// The public host that serves `/stock-images/*` and proxies `/api/v1/image/*`
// to crm-api. This is the customer-facing **support** origin — deliberately
// NOT the dashboard origin: email images are dereferenced by external
// recipients' mail clients, and the internal dashboard URL
// (`crm.xopnetworks.com`) must never leak into an outbound email. The stock
// photos are bundled in the support center's `public/stock-images/`
// (xop-edge-customer/apps/portal-web) so support.xopnetworks.com serves them, and
// support proxies `/api/v1/image/*` to crm-api (verified 2026-06-06). Kept in
// lock-step with crm-api's PUBLIC_URL fallback. (Moved off the apex
// `crm.xopnetworks.com` to `support.xopnetworks.com` 2026-06-06 per the "never serve email
// assets from the dashboard" rule.)
export const PUBLIC_ASSET_HOST = "https://support.xopnetworks.com";

/**
 * Anchor a possibly-relative asset URL to {@link PUBLIC_ASSET_HOST}.
 *
 * - Absolute `http(s)://` URLs and `data:`/`blob:` URIs pass through untouched.
 * - Root-relative URLs (`/stock-images/x.jpg`, `/api/v1/image/k.png`) gain the
 *   public host.
 * - `undefined`/empty → `""` (so `{logo && <img src={toAbsoluteUrl(logo)} />}`
 *   guards still collapse to "no logo" rather than a broken image).
 * - Bare relative URLs (no leading slash) are returned as-is — we can't know
 *   their base; in practice every caller passes a root-relative or absolute URL.
 */
export const toAbsoluteUrl = (url: string | null | undefined): string => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/")) return `${PUBLIC_ASSET_HOST}${url}`;
  return url;
};

/**
 * Anchor an asset URL for the in-EDITOR PREVIEW (NOT for outbound email HTML).
 *
 * Why this differs from {@link toAbsoluteUrl}: the tenant logo and uploaded
 * newsletter images live in crm-api and are served at `/api/v1/image/<key>`.
 * In a sent email those are CID-inlined by the backend, so the URL there is a
 * never-fetched fallback and `toAbsoluteUrl` (→ {@link PUBLIC_ASSET_HOST}) is
 * correct. But the editor PREVIEW fetches the URL over HTTP — and
 * `support.xopnetworks.com/api/v1/image/*` does NOT proxy to crm-api's image
 * controller (it returns a generic "Resource not found"), so the logo renders
 * as a broken image while bundled `/stock-images/*` (served by support
 * directly) render fine. The DASHBOARD origin, by contrast, DOES proxy
 * `/api/v1/*` to crm-api (that's how the whole CRM sub-app reaches it), so the
 * preview must dereference logos/uploads through the dashboard, not support.
 *
 * - `/api/v1/image/*` → dashboard same-origin (`env.apiUrl` on desktop, the
 *   current origin on web) so it reaches the SAME crm-api the data came from.
 * - `/stock-images/*` and other root-relative → the public asset host (bundled
 *   there; identical on every deploy).
 * - Absolute / data: / blob: pass through untouched.
 */
export const toPreviewImageUrl = (url: string | null | undefined): string => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/api/v1/image")) {
    const base =
      env.apiUrl ||
      (typeof window !== "undefined" ? window.location.origin : "");
    return `${base}${url}`;
  }
  if (url.startsWith("/")) return `${PUBLIC_ASSET_HOST}${url}`;
  return url;
};
